import type { RouteComponentProps } from "react-router-dom";

import type { BreadcrumbTrailItem } from "~/common/redesign-v1";

import { createRedesignEntry } from "../redesign-page-entry";

/**
 * Route entries for the legacy-body adapters in `./legacy-page-redesign`.
 *
 * Deprecated entries are not referenced by `app/index.tsx`; they provide an explicit legacy fallback.
 */

type PatientRoute = RouteComponentProps<{ patient_id: string }>;

function patientTrail(patientId: string | undefined): BreadcrumbTrailItem[] | undefined {
  if (!patientId) return undefined;
  return [
    {
      id: "redesign.shell.patient",
      defaultMessage: "Patient {id}",
      values: { id: patientId },
      to: `/pages/patient/${patientId}`,
    },
  ];
}

/** @deprecated Use `redesign-v1/payments/payments-entry`. */
export const PaymentsRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).PaymentsRedesign }), {
  testId: "rd-payments-loading",
  shell: { activeNav: "payments" },
});

/** @deprecated Use `redesign-v1/delivery/delivery-entry`. */
export const ShipmentsRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).ShipmentsRedesign }), {
  testId: "rd-delivery-loading",
  shell: { activeNav: "delivery" },
});

export const ChatRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).ChatRedesign }), {
  testId: "rd-chat-loading",
  shell: { activeNav: "chat", fullHeight: true },
});

export const PatientDeliveriesRedesignEntry = createRedesignEntry<PatientRoute>(async () => ({ default: (await import("./legacy-page-redesign")).PatientDeliveriesRedesign }), {
  testId: "rd-patient-deliveries-loading",
  shell: (props) => {
    const patientId = props.match?.params?.patient_id;
    return {
      activeNav: "patients",
      breadcrumbTrail: patientTrail(patientId),
      breadcrumbLeaf: { id: "redesign.shell.all_deliveries", defaultMessage: "All shipments" },
    };
  },
});

export const MeetingsFlowRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).MeetingsFlowRedesign }), {
  testId: "rd-meetings-flow-loading",
  // Meeting sub-routes use breadcrumbs; the listing keeps its own page chrome.
  shell: { activeNav: "consultations", breadcrumbLeaf: { id: "meetings.detail.title", defaultMessage: "Meeting details" } },
});

// Instructions reuses the manuals feed but renders its own redesign page.
export const ManualsRedesignEntry = createRedesignEntry(() => import("~/components/redesign-v1/instructions/instructions-page"), {
  testId: "rd-instructions-loading",
  shell: { activeNav: "instructions" },
});

/** @deprecated Use `redesign-v1/documents/documents-entry`. */
export const DocumentsRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).DocumentsRedesign }), {
  testId: "rd-documents-loading",
  shell: { activeNav: "documents" },
});

// Sub-pages reached from redesign pages must retain the redesign shell.
export const PlanCorrectionRedesignEntry = createRedesignEntry<PatientRoute>(async () => ({ default: (await import("./legacy-page-redesign")).PlanCorrectionRedesign }), {
  testId: "rd-plan-correction-loading",
  shell: (props) => ({
    activeNav: "patients",
    breadcrumbTrail: patientTrail(props.match?.params?.patient_id),
    breadcrumbLeaf: { id: "treatment.correct.header", defaultMessage: "Adjust treatment" },
  }),
});

export const PlanRetainersRedesignEntry = createRedesignEntry<PatientRoute>(async () => ({ default: (await import("./legacy-page-redesign")).PlanRetainersRedesign }), {
  testId: "rd-plan-retainers-loading",
  shell: (props) => ({
    activeNav: "patients",
    breadcrumbTrail: patientTrail(props.match?.params?.patient_id),
    breadcrumbLeaf: { id: "pat.retainers.page.header", defaultMessage: "Retainers" },
  }),
});

export const PlanPowerCapRedesignEntry = createRedesignEntry<PatientRoute>(async () => ({ default: (await import("./legacy-page-redesign")).PlanPowerCapRedesign }), {
  testId: "rd-plan-power-cap-loading",
  shell: (props) => ({
    activeNav: "patients",
    breadcrumbTrail: patientTrail(props.match?.params?.patient_id),
    breadcrumbLeaf: { id: "pat.power_cap.page.header", defaultMessage: "Power cap" },
  }),
});

export const AdditionalAlignersOrderRedesignEntry = createRedesignEntry<PatientRoute>(async () => ({ default: (await import("./legacy-page-redesign")).AdditionalAlignersOrderRedesign }), {
  testId: "rd-additional-aligners-order-loading",
  shell: (props) => ({
    activeNav: "patients",
    breadcrumbTrail: patientTrail(props.match?.params?.patient_id),
    breadcrumbLeaf: { id: "pat.additional.aligners.page.header", defaultMessage: "Replacement Aligners" },
  }),
});

export const AdditionalAlignersTableRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).AdditionalAlignersTableRedesign }), {
  testId: "rd-additional-aligners-table-loading",
  shell: { activeNav: "patients" },
});

/** @deprecated Use `redesign-v1/events/event-detail-entry`. */
export const EventDetailRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).EventDetailRedesign }), {
  testId: "rd-event-detail-loading",
  // Event sub-routes use breadcrumbs; the listing keeps its own page chrome.
  shell: { activeNav: "learning", breadcrumbLeaf: { id: "redesign.shell.event_detail", defaultMessage: "Description" } },
});

// Payment-provider return routes must be routed ahead of
// `EventDetailEntry` in app/index.tsx (more specific paths win in the Switch).
export const EventPaymentStatusRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).EventPaymentStatusRedesign }), {
  testId: "rd-event-payment-status-loading",
  shell: { activeNav: "learning" },
});

export const EventPaymentSuccessRedesignEntry = createRedesignEntry<RouteComponentProps<{ event_id: string; hash: string }>>(
  async () => ({ default: (await import("./legacy-page-redesign")).EventPaymentSuccessRedesign }),
  { testId: "rd-event-payment-success-loading", shell: { activeNav: "learning" } },
);

export const EventPaymentErrorRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).EventPaymentErrorRedesign }), {
  testId: "rd-event-payment-error-loading",
  shell: { activeNav: "learning" },
});

// Post-payment redirect landing pages for the doctor/patient invoice flow (YooKassa's `return_url` after
// `payments-page.tsx`'s batch checkout). Keep them in the redesign shell after the provider redirect.
export const PaymentInvoiceStatusRedesignEntry = createRedesignEntry<RouteComponentProps<{ patient_id: string; invoice_id: string }>>(
  async () => ({ default: (await import("./legacy-page-redesign")).PaymentInvoiceStatusRedesign }),
  { testId: "rd-payment-invoice-status-loading", shell: { activeNav: "payments" } },
);

export const PaymentInvoiceBatchStatusRedesignEntry = createRedesignEntry<RouteComponentProps<{ batch_id: string }>>(
  async () => ({ default: (await import("./legacy-page-redesign")).PaymentInvoiceBatchStatusRedesign }),
  { testId: "rd-payment-invoice-batch-status-loading", shell: { activeNav: "payments" } },
);

/** @deprecated Use `redesign-v1/promo/promo-entry`. */
export const PromoMaterialsRedesignEntry = createRedesignEntry(async () => ({ default: (await import("./legacy-page-redesign")).PromoMaterialsRedesign }), {
  testId: "rd-materials-loading",
  shell: { activeNav: "materials" },
});
